import {
  Firestore,
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit as firestoreLimit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { ContentDao } from '@/api/daos/content-dao';
import {
  ContentDiff,
  ContentSnapshot,
  createDiff,
  createSnapshot,
  generatePatch,
  getCurrentDayKey,
  isLargePatch,
  reconstructText,
} from '@/lib/text-versioning';
import type { Novel, ContentDocument } from '@/lib/types';

const COMPACTION_THRESHOLD = 200;

export class FirebaseContentVersioning {
  constructor(private firestore: Firestore) {}

  async getOrCreateContent(
    ownerId: string,
    novel: Novel,
    contentId: string,
    chapterId: string | null,
    initialContent?: string
  ): Promise<ContentDocument> {
    const contentDao = new ContentDao(this.firestore);
    try {
      const item = await contentDao.getContent<ContentDocument>(ownerId, novel, contentId);
      if (item) return item;
    } catch (e) {
      console.debug('Content document not found, creating new one.');
    }

    const dayKey = getCurrentDayKey();
    const newDoc: ContentDocument = {
      id: contentId,
      name: 'Untitled',
      scratchpad: initialContent || '',
      scratchpadVersion: 0,
      currentDayKey: dayKey,
      chapterId,
      sceneIds: [],
      wordCount: this.calculateWordCount(initialContent || ''),
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    await contentDao.createContent(ownerId, novel, newDoc);

    const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novel.id}/contents/${contentId}/snapshots/${dayKey}`);
    await setDoc(snapshotRef, {
      ...createSnapshot(newDoc.scratchpad, dayKey),
      createdAt: serverTimestamp(),
    });

    return newDoc;
  }

  async saveContentVersion(
    ownerId: string,
    novel: Novel,
    contentId: string,
    newContent: string,
    isAutomatic: boolean,
    expectedVersion?: number,
    skipHistory?: boolean
  ): Promise<boolean> {
    const contentDao = new ContentDao(this.firestore);
    const novelId = novel.id;

    let textDoc = await contentDao.getItem<ContentDocument>(ownerId, novel, 'narrative', 'contents', contentId);
    if (!textDoc) {
      textDoc = await this.getOrCreateContent(ownerId, novel, contentId, null, newContent);
    }

    const currentVersion = typeof textDoc.scratchpadVersion === 'number' && !isNaN(textDoc.scratchpadVersion)
      ? textDoc.scratchpadVersion
      : 0;

    if (expectedVersion !== undefined && currentVersion !== expectedVersion) {
      throw new Error(`CONTENT_VERSION_CONFLICT expected=${expectedVersion} actual=${currentVersion}`);
    }

    const dayKey = getCurrentDayKey();
    const lastContent = textDoc.scratchpad || '';
    let itemId: string | null = null;

    let needsSnapshot = false;
    if (textDoc.currentDayKey !== dayKey) {
      needsSnapshot = true;
    } else {
      const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}`);
      const snapshotSnap = await getDoc(snapshotRef);
      if (!snapshotSnap.exists()) {
        needsSnapshot = true;
      }
    }

    if (needsSnapshot) {
      const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}`);
      await setDoc(snapshotRef, {
        ...createSnapshot(lastContent, dayKey),
        createdAt: serverTimestamp(),
      });
    }

    const patchText = generatePatch(lastContent, newContent);
    if (!patchText) {
      // Identical content is not a save. Avoid timestamp-only writes that wake
      // every collaborator listener and can create multi-tab feedback loops.
      return false;
    }

    const newVersion = currentVersion + 1;

    if (isLargePatch(patchText)) {
      const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}-large`);
      await setDoc(snapshotRef, {
        ...createSnapshot(newContent, `${dayKey}-large`),
        createdAt: serverTimestamp(),
      });
    } else {
      const diffData = createDiff(newVersion, patchText, newContent, isAutomatic, ownerId);
      const diffsRef = collection(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/diffs/${dayKey}/items`);
      const diffDoc = await addDoc(diffsRef, {
        ...diffData,
        ts: serverTimestamp(),
      });
      itemId = diffDoc.id;
    }

    const wordCount = this.calculateWordCount(newContent);
    const updateData: Partial<ContentDocument> = {
      scratchpad: newContent,
      scratchpadVersion: newVersion,
      currentDayKey: dayKey,
      wordCount,
    };

    await contentDao.updateContent(ownerId, novel, contentId, updateData);

    if (itemId && !skipHistory) {
      await contentDao.updateItem(ownerId, novel, 'narrative', 'contents', contentId, {
        [`history.${dayKey}`]: arrayUnion({
          id: itemId,
          seq: newVersion,
          dateTime: new Date().toISOString(),
        })
      });
    }

    // Run compaction in background so it doesn't block the save process
    this.checkAndCompact(ownerId, novelId, contentId, dayKey).catch((err) => {
      console.warn(`Background compaction failed for ${contentId}:`, err);
    });

    return true;
  }

  async getDiffsForDay(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string,
    limitCount = 20
  ): Promise<ContentDiff[]> {
    const diffsQuery = query(
      collection(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/diffs/${dayKey}/items`),
      orderBy('seq', 'desc'),
      firestoreLimit(limitCount)
    );
    const diffsSnap = await getDocs(diffsQuery);
    return diffsSnap.docs.map((d) => d.data() as ContentDiff);
  }

  async getRecentHistory(
    ownerId: string,
    novelId: string,
    contentId: string,
    limitCount = 50
  ): Promise<ContentDiff[]> {
    const dayKey = getCurrentDayKey();
    return this.getDiffsForDay(ownerId, novelId, contentId, dayKey, limitCount);
  }

  async getSnapshotForDay(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string
  ): Promise<ContentSnapshot | null> {
    const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}`);
    const snapshotSnap = await getDoc(snapshotRef);
    return snapshotSnap.exists() ? (snapshotSnap.data() as ContentSnapshot) : null;
  }

  async reconstructTextAtPoint(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string,
    seq: number
  ): Promise<{ content: string; previousContent: string; success: boolean }> {
    try {
      const snapshot = await this.getSnapshotForDay(ownerId, novelId, contentId, dayKey);
      if (!snapshot) return { content: '', previousContent: '', success: false };

      const diffsRef = collection(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/diffs/${dayKey}/items`);
      const diffsQuery = query(diffsRef, orderBy('seq', 'asc'), where('seq', '<=', seq));
      const diffsSnap = await getDocs(diffsQuery);
      const diffs = diffsSnap.docs.map((d) => d.data() as ContentDiff);

      const { content, success } = reconstructText(snapshot, diffs);
      if (!success) return { content: '', previousContent: '', success: false };

      let previousContent = '';
      if (seq > 0) {
        const prevDiffs = diffs.filter((d) => d.seq < seq);
        const res = reconstructText(snapshot, prevDiffs);
        if (res.success) previousContent = res.content;
      } else {
        previousContent = snapshot.baseContent;
      }

      return { content, previousContent, success: true };
    } catch (error) {
      console.error('Reconstruction failed:', error);
      return { content: '', previousContent: '', success: false };
    }
  }

  private async checkAndCompact(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string
  ): Promise<void> {
    const diffsQuery = query(
      collection(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/diffs/${dayKey}/items`),
      orderBy('seq', 'asc')
    );
    const diffsSnap = await getDocs(diffsQuery);

    if (diffsSnap.size > COMPACTION_THRESHOLD) {
      const snapshotRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}`);
      const snapshotSnap = await getDoc(snapshotRef);

      if (snapshotSnap.exists()) {
        const snapshot = snapshotSnap.data() as ContentSnapshot;
        const diffs = diffsSnap.docs.map((d) => d.data() as ContentDiff);
        const { content, success } = reconstructText(snapshot, diffs);

        if (success) {
          const compactedRef = doc(this.firestore, `users/${ownerId}/novels/${novelId}/contents/${contentId}/snapshots/${dayKey}-compacted`);
          await setDoc(compactedRef, {
            ...createSnapshot(content, `${dayKey}-compacted`),
            createdAt: serverTimestamp(),
          });
        }
      }
    }
  }

  private calculateWordCount(content: string): number {
    if (!content) return 0;
    const text = content.replace(/<[^>]*>/g, ' ');
    return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
  }
}
